import React from 'react';
import { Text, View } from 'react-native';
import { TemplateHash } from './types';
import {
    formatDateWithTimeZone,
    notificationTitleHash,
    updateTemplateHash,
} from './utils';
import { NotificationTemplateForAdmin } from '@sapphicsavvy/business';
import { getColors } from '../../../../../constants/colors';
import { StyleSheet } from 'react-native';
import { mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { textStyles } from '../../../../../constants/textStyles';
import { Switch } from './Switch';
import { MB_TextInput } from '@mightybyte/rnw.components.text-input';
import { MB_Button } from '@mightybyte/rnw.components.button';
import { quarterSharedStyles } from '../quarter/quarterSharedStyles';

interface Props {
    template: NotificationTemplateForAdmin;
    setTemplate: React.Dispatch<React.SetStateAction<TemplateHash | null>>;
    onTestPress: () => void;
}

const TITLE_MAX_CHARS = 60;
const MESSAGE_MAX_CHARS = 150;

const COLORS = getColors();

export function NotificationCard({
    template,
    setTemplate,
    onTestPress,
}: Props) {
    const { type, defaultSendDate, defaultMessage, defaultTitle } = template;

    const updateHash = <K extends keyof NotificationTemplateForAdmin>(
        field: K,
        value: NotificationTemplateForAdmin[K],
    ) => {
        updateTemplateHash(setTemplate, {
            type,
            fields: { [field]: value } as Partial<NotificationTemplateForAdmin>,
        });
    };

    const handleTitleChange = (value: string) => {
        if (value.length <= TITLE_MAX_CHARS) {
            updateHash('customTitle', value);
        }
    };

    const handleMessageChange = (value: string) => {
        if (value.length <= MESSAGE_MAX_CHARS) {
            updateHash('customMessage', value);
        }
    };

    const titleCharsRemaining =
        TITLE_MAX_CHARS - (template.customTitle?.length || 0);

    const messageCharsRemaining =
        MESSAGE_MAX_CHARS - (template.customMessage?.length || 0);

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <Text style={styles.headerText}>
                    {notificationTitleHash[type]}
                </Text>
                <Switch
                    isToggled={template.isActive}
                    onToggle={(value) => updateHash('isActive', value)}
                />
            </View>
            <View style={styles.body}>
                <Text style={styles.defaultText}>Default Settings</Text>

                <View style={styles.defaultContainer}>
                    <Text style={styles.defaultTime}>
                        Time: {formatDateWithTimeZone(defaultSendDate)}
                    </Text>
                    <Text style={styles.defaultTitle}>{defaultTitle}</Text>
                    <Text style={styles.defaultMessage}>{defaultMessage}</Text>
                </View>

                <Text style={styles.defaultText}>Customize</Text>

                <View style={styles.inputWrapper}>
                    <View style={styles.customTitle}>
                        <View style={styles.labelContainer}>
                            <Text style={styles.label}>Custom Title</Text>
                            <Text style={styles.charCounter}>
                                {titleCharsRemaining} characters remaining
                            </Text>
                        </View>
                        <MB_TextInput
                            style={styles.customTitleInput}
                            value={template.customTitle || ''}
                            onChangeText={handleTitleChange}
                            textInputStyle={styles.inputText}
                            maxLength={TITLE_MAX_CHARS}
                        />
                    </View>

                    <View style={styles.customMessage}>
                        <View style={styles.labelContainer}>
                            <Text style={styles.label}>Custom Message</Text>
                            <Text style={styles.charCounter}>
                                {messageCharsRemaining} characters remaining
                            </Text>
                        </View>
                        <MB_TextInput
                            multiline
                            style={styles.customMessageInput}
                            value={template.customMessage || ''}
                            onChangeText={handleMessageChange}
                            textInputStyle={styles.inputText}
                            maxLength={MESSAGE_MAX_CHARS}
                        />
                    </View>
                </View>
            </View>
            <MB_Button
                title={'Test Notification'}
                onPress={onTestPress}
                style={[
                    quarterSharedStyles.button,
                    { marginTop: 10, alignSelf: 'center' },
                ]}
                textStyle={quarterSharedStyles.buttonText}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: COLORS.white,
        borderWidth: 4,
        borderColor: COLORS.white,
        borderRadius: 15,
        flexGrow: 1,
        marginBottom: 15,
        height: '100%',
    },
    header: {
        backgroundColor: '#FFF4F0',
        borderTopRightRadius: 15,
        borderTopLeftRadius: 15,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 15,
    },
    headerText: mbTextStyles([
        textStyles.largerText,
        {
            color: COLORS.orange,
            fontSize: 20,
            paddingBottom: 8,
        },
    ]),
    defaultText: mbTextStyles([
        textStyles.tinyText,
        {
            fontSize: 14,
            paddingVertical: 8,
            color: '#202422',
            textAlign: 'left',
        },
    ]),
    defaultContainer: {
        height: 150,
        padding: 15,
        paddingLeft: 30,
        borderWidth: 1,
        borderRadius: 5,
        borderColor: COLORS.orange,
    },
    defaultTime: mbTextStyles([
        textStyles.tinyText,
        {
            fontSize: 16,
            paddingBottom: 15,
            color: '#0000008C',
            textAlign: 'left',
        },
    ]),
    defaultTitle: mbTextStyles([
        textStyles.tinyText,
        {
            fontSize: 14,
            paddingBottom: 8,
            color: '#202422',
            textAlign: 'left',
        },
    ]),
    defaultMessage: mbTextStyles([
        textStyles.tinyText,
        {
            fontSize: 14,
            color: '#202422',
            textAlign: 'left',
        },
    ]),
    body: {
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    inputWrapper: {
        backgroundColor: '#F3F3F3',
        padding: 15,
        borderRadius: 5,
    },
    labelContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    label: mbTextStyles([
        textStyles.tinyText,
        {
            fontSize: 14,
            color: '#202422',
            textAlign: 'left',
        },
    ]),
    charCounter: mbTextStyles([
        textStyles.tinyText,
        {
            fontSize: 12,
            color: '#6A6A6A',
            textAlign: 'right',
        },
    ]),
    customTitle: {},
    customMessage: {},
    customTitleInput: {
        width: '100%',
        backgroundColor: COLORS.white,
        padding: 0,
        margin: 0,
        height: 40,
        marginBottom: 20,
        borderRadius: 5,
    },
    customMessageInput: {
        width: '100%',
        height: 120,
        backgroundColor: COLORS.white,
        borderRadius: 5,
    },
    inputText: mbTextStyles([
        textStyles.tinyText,
        {
            fontSize: 14,
            color: '#202422',
            textAlign: 'left',
        },
    ]),
});
